
import { useState, useEffect } from 'react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from '@/components/ui/use-toast';
import { carMakes, carModels, years, carLogos } from "@/utils/carData";
import { supabase } from '@/integrations/supabase/client';
import BookingResultModal from '@/components/booking/BookingResultModal';

const shopsData = [{
  name: "A.S. MOTOR",
  city: "Bethlehem",
  address: "Doha"
}, {
  name: "Modern Super Clean Station",
  city: "Tulkarm",
  address: "Shweika Street"
}, {
  name: "Al-Mustaqbal",
  city: "Ramallah",
  address: "Al-Irsal Street"
}, {
  name: "Sara Company for Oils and Filters",
  city: "Jenin",
  address: "Industrial Zone"
}, {
  name: "Dwaikat Station",
  city: "Nablus",
  address: "Al-Quds Street"
}, {
  name: "Nabil Abu Al-Hassan Garage",
  city: "Tulkarm",
  address: "Near Aktaba Roundabout"
}, {
  name: "Al-Okhwa Oil & Filters Trading Co.",
  city: "Hebron",
  address: "Wadi Abu Ketila"
}, {
  name: "Al-Aamer",
  city: "Nablus",
  address: "Jamal Abdel Nasser Street"
}, {
  name: "SH Auto Dynamic",
  city: "Qalqilya",
  address: "Al-Wad Street"
}, {
  name: "Al-Aqsa",
  city: "Ramallah",
  address: "Nablus Street"
}, {
  name: "Al-Edra Oil & Filter Co.",
  city: "Hebron",
  address: "Yatta"
}, {
  name: "Al-Masa",
  city: "Nablus",
  address: "Amman Street"
}, {
  name: "Rashad Fakhouri",
  city: "Jenin",
  address: "Jaba"
}, {
  name: "Mansiya Oil & Filter Shop",
  city: "Hebron",
  address: "Al-Dhahiriya"
}, {
  name: "Hosni Abu Al-Hassan Station",
  city: "Tulkarm",
  address: "Al-Haddadin Street"
}, {
  name: "Al-Israa Vehicle Maintenance",
  city: "Bethlehem",
  address: "Al-Khader"
}, {
  name: "Abu Zneid Auto Lube Station",
  city: "Hebron",
  address: "Al-Tahrir Roundabout"
}, {
  name: "Banoura Oil & Filter Shop",
  city: "Bethlehem",
  address: "Artas"
}];

const cities = ["Bethlehem", "Tulkarm", "Ramallah", "Jenin", "Nablus", "Hebron", "Qalqilya"];
import { useAuth } from '@/hooks/useAuth';

const N8N_WEBHOOK_URL = 'https://autocarepalestine.app.n8n.cloud/webhook-test/BookingMassege';

const Booking = () => {
  const [isLoaded, setIsLoaded] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [resultMessage, setResultMessage] = useState('');

  // === Autofill user info ===
  const { user } = useAuth();
  const [city, setCity] = useState('');
  const [shopKey, setShopKey] = useState('');
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [timeSlot, setTimeSlot] = useState('');
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleYear, setVehicleYear] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const timeSlots = ['09:00 AM', '10:00 AM', '11:00 AM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM'];
  const filteredShopOptions = city ? shopsData.filter(s => s.city === city) : [];
  const getShopByKey = (key: string) => {
    return shopsData.find(shop => `${shop.name} – ${shop.address}` === key);
  };

  // Autofill user details from useAuth when user changes
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  const sendWebhookNotification = async (bookingData: any) => {
    try {
      console.log('Sending webhook notification to n8n...');
      await fetch(N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'no-cors',
        body: JSON.stringify({
          ...bookingData,
          timestamp: new Date().toISOString(),
          source: 'booking_page'
        }),
      });
      console.log('Webhook notification sent successfully');
    } catch (error) {
      console.error('Failed to send webhook notification:', error);
    }
  };

  // Updated handleSubmit to save directly to Supabase
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!city) {
      toast({
        title: "Error",
        description: "Please select a city",
        variant: "destructive"
      });
      return;
    }
    if (!shopKey) {
      toast({
        title: "Error",
        description: "Please select a shop",
        variant: "destructive"
      });
      return;
    }
    if (!date) {
      toast({
        title: "Error",
        description: "Please select a date for your appointment",
        variant: "destructive"
      });
      return;
    }
    if (!timeSlot) {
      toast({
        title: "Error",
        description: "Please select a time slot",
        variant: "destructive"
      });
      return;
    }
    if (!vehicleYear) {
      toast({
        title: "Error",
        description: "Please provide vehicle year",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const shop = getShopByKey(shopKey);
      
      console.log('Starting booking submission...');
      console.log('Shop data:', shop);
      console.log('Form data validation passed');

      // Create properly typed booking data object with simpler structure
      const bookingData = {
        "Full Name": name.trim() || '',
        "Email": email.trim() || '',
        "Phone Number": phone.trim() ? parseInt(phone.trim()) : null,
        "Shop Name": shop?.name || '',
        "City": shop?.city || city,
        "Date": date ? format(date, 'yyyy-MM-dd') : null,
        "Time": timeSlot || '',
        "Vehicle Make": vehicleMake || '',
        "Vehicle Model": vehicleModel || '',
        "Year": vehicleYear ? parseInt(vehicleYear) : null,
        "Oil Brand": '',
        "Oil Viscosity": '',
        "Payment Method": 'cash',
        "Payment Status": 'pending',
        "Notes": notes.trim() || null,
        "amount": 0
      };

      console.log('Prepared booking data:', JSON.stringify(bookingData, null, 2));

      // Try to save to Supabase with better error handling
      console.log('Attempting to save to Supabase...');
      
      const { data: result, error } = await supabase
        .from('New Bookings')
        .insert([bookingData])
        .select();

      console.log('Supabase response data:', result);
      console.log('Supabase response error:', error);

      if (error) {
        console.error('Supabase booking error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        setBookingSuccess(false);
        setResultMessage(`Database error: ${error.message}. Please try again.`);
        setShowResultModal(true);
        return;
      }

      console.log('Booking saved successfully:', result);

      // Send webhook notification to n8n
      await sendWebhookNotification(bookingData);

      // Show success modal
      setBookingSuccess(true);
      setResultMessage('تم حجز موعدك بنجاح! سيتم التواصل معك قريباً لتأكيد الموعد.');
      setShowResultModal(true);

      // Reset form
      setCity('');
      setShopKey('');
      setDate(undefined);
      setTimeSlot('');
      setVehicleMake('');
      setVehicleModel('');
      setVehicleYear('');
      setName('');
      setEmail('');
      setPhone('');
      setNotes('');
      
    } catch (error: any) {
      console.error('Booking submission error:', error);
      console.error('Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
      setBookingSuccess(false);
      setResultMessage(`Unexpected error: ${error?.message || 'Unknown error'}. Please try again.`);
      setShowResultModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResultModalClose = () => {
    setShowResultModal(false);
  };

  return (
    <div className={`min-h-screen bg-white ${isLoaded ? 'animate-fade-in' : 'opacity-0'}`}>
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="page-container max-w-6xl">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-autocare-blue mb-4">احجز موعدك</h1>
            <p className="text-gray-600 max-w-3xl mx-auto">
              احجز موعد تغيير زيت مركبتك في خطوات بسيطة. اختر التاريخ والوقت والورشة المناسبة لك.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="pt-6">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="city">المدينة</Label>
                        <Select onValueChange={v => {
                          setCity(v);
                          setShopKey('');
                        }} value={city}>
                          <SelectTrigger>
                            <SelectValue placeholder="اختر مدينة" />
                          </SelectTrigger>
                          <SelectContent>
                            {cities.map(cityOption => (
                              <SelectItem key={cityOption} value={cityOption}>{cityOption}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label htmlFor="shopName">اسم الورشة</Label>
                        <Select onValueChange={setShopKey} value={shopKey} disabled={!city}>
                          <SelectTrigger>
                            <SelectValue placeholder={city ? "اختر ورشة" : "اختر المدينة أولاً"} />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredShopOptions.map(shop => (
                              <SelectItem key={`${shop.name} – ${shop.address}`} value={`${shop.name} – ${shop.address}`}>
                                {shop.name} – {shop.address}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label htmlFor="name">الاسم الكامل</Label>
                        <Input 
                          id="name" 
                          value={name} 
                          onChange={e => setName(e.target.value)} 
                          placeholder="أحمد محمد" 
                          required 
                          autoComplete="name"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="email">البريد الإلكتروني</Label>
                          <Input 
                            id="email" 
                            type="email" 
                            value={email} 
                            onChange={e => setEmail(e.target.value)} 
                            placeholder="ahmed@example.com" 
                            required 
                            autoComplete="email"
                          />
                        </div>
                        <div>
                          <Label htmlFor="phone">رقم الهاتف</Label>
                          <Input 
                            id="phone" 
                            value={phone} 
                            onChange={e => setPhone(e.target.value)} 
                            placeholder="+972 59 XXX XXXX" 
                            required 
                            autoComplete="tel"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="vehicle-make">ماركة المركبة</Label>
                          <Select value={vehicleMake} onValueChange={value => {
                            setVehicleMake(value);
                            setVehicleModel('');
                          }}>
                            <SelectTrigger>
                              <SelectValue placeholder="اختر الماركة">
                                {vehicleMake ? (
                                  <div className="flex items-center">
                                    {carLogos[vehicleMake] && (
                                      <img 
                                        src={carLogos[vehicleMake]} 
                                        alt={`${vehicleMake} logo`}
                                        className="h-5 w-5 mr-2 object-contain"
                                      />
                                    )}
                                    <span>{vehicleMake}</span>
                                  </div>
                                ) : (
                                  "اختر الماركة"
                                )}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {carMakes.map(m => (
                                <SelectItem key={m} value={m}>
                                  <div className="flex items-center">
                                    {carLogos[m] && (
                                      <img 
                                        src={carLogos[m]} 
                                        alt={`${m} logo`}
                                        className="h-5 w-5 mr-2 object-contain"
                                      />
                                    )}
                                    <span>{m}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label htmlFor="vehicle-model">موديل المركبة</Label>
                          <Select value={vehicleModel} onValueChange={setVehicleModel} disabled={!vehicleMake}>
                            <SelectTrigger>
                              <SelectValue placeholder="اختر الموديل" />
                            </SelectTrigger>
                            <SelectContent>
                              {vehicleMake && carModels[vehicleMake] ? carModels[vehicleMake].map(mod => (
                                <SelectItem key={mod} value={mod}>{mod}</SelectItem>
                              )) : null}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label htmlFor="vehicle-year">السنة</Label>
                          <Select value={vehicleYear} onValueChange={setVehicleYear}>
                            <SelectTrigger>
                              <SelectValue placeholder="اختر السنة" />
                            </SelectTrigger>
                            <SelectContent>
                              {years.map(y => (
                                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div>
                        <Label>اختر التاريخ</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {date ? format(date, 'PPP') : <span>اختر التاريخ</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent side="bottom" align="start" sideOffset={8} collisionPadding={16} className="w-auto p-0 z-[9999] pointer-events-auto">
                            <Calendar 
                              mode="single" 
                              selected={date} 
                              onSelect={setDate} 
                              initialFocus 
                              className="pointer-events-auto"
                              disabled={date => {
                                return date < new Date() || date.getDay() === 5;
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      
                      <div>
                        <Label htmlFor="time">اختر الوقت</Label>
                        <Select onValueChange={setTimeSlot} value={timeSlot}>
                          <SelectTrigger>
                            <SelectValue placeholder="اختر الوقت">
                              <Clock className="mr-2 h-4 w-4" />
                              {timeSlot || "اختر الوقت"}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {timeSlots.map(slot => (
                              <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label htmlFor="notes">ملاحظات إضافية</Label>
                        <Input 
                          id="notes" 
                          value={notes} 
                          onChange={e => setNotes(e.target.value)} 
                          placeholder="أي طلبات خاصة أو معلومات إضافية" 
                        />
                      </div>
                    </div>
                    
                    <Button type="submit" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? 'جاري الحجز...' : 'احجز الموعد'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
              
              <div dir="rtl" className="mb-6 text-autocare-dark-blue font-bold text-xl md:text-2xl text-right bg-autocare-yellow py-[5px] px-0 my-[15px]">
                تنويه هام: هذا الموقع هو جزء من مشروع تخرج طلابي في جامعة النجاح الوطنية، ويُستخدم لأغراض أكاديمية فقط، عملية الحجز ليست رسمية أو معتمدة، ولن يتم التواصل أو تنفيذ الطلبات بشكل فعلي
              </div>
            </div>
            
            <div>
              <Card>
                <CardContent className="pt-6 text-autocare-blue">
                  <h3 className="text-xl font-bold mb-4">لماذا تختار خدمتنا؟</h3>
                  <ul className="space-y-4">
                    <li className="flex">
                      <div className="rounded-full bg-autocare-blue/10 p-3 mr-3">
                        <Clock className="h-5 w-5 text-autocare-blue" />
                      </div>
                      <div>
                        <h4 className="font-medium">راحة وسهولة</h4>
                        <p className="text-sm text-gray-600">احجز موعدك بسهولة عبر الإنترنت.</p>
                      </div>
                    </li>
                    <li className="flex">
                      <div className="rounded-full bg-autocare-blue/10 p-3 mr-3">
                        <CalendarIcon className="h-5 w-5 text-autocare-blue" />
                      </div>
                      <div>
                        <h4 className="font-medium">تذكيرات بالمواعيد</h4>
                        <p className="text-sm text-gray-600">تذكيرات آلية لضمان عدم نسيانك للموعد.</p>
                      </div>
                    </li>
                    <li className="flex">
                      <div className="rounded-full bg-autocare-blue/10 p-3 mr-3">
                        <Clock className="h-5 w-5 text-autocare-blue" />
                      </div>
                      <div>
                        <h4 className="font-medium">توفير الوقت</h4>
                        <p className="text-sm text-gray-600">وفر وقتك وجهدك في البحث عن ورشة.</p>
                      </div>
                    </li>
                  </ul>
                  
                  <div className="border-t border-gray-200 mt-6 pt-6">
                    <h3 className="text-xl font-bold mb-4">ساعات العمل</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>السبت - الخميس</span>
                        <span>8:00 صباحًا - 6:00 مساءً</span>
                      </div>
                      <div className="flex justify-between">
                        <span>الجمعة</span>
                        <span>مغلق</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      {/* Booking Result Modal */}
      <BookingResultModal 
        isOpen={showResultModal}
        isSuccess={bookingSuccess}
        message={resultMessage}
        onClose={handleResultModalClose}
      />
    </div>
  );
};

export default Booking;
